/**
 * Calendar dates in a named zone.
 *
 * The stock order report is run "by day", and a day is the dealership's:
 * local midnight to the next local midnight in `DEALERSHIP_TIMEZONE`, which
 * is 23 hours long the day Chicago springs forward and 25 the day it falls
 * back. Instants are stored in UTC, so the report needs to turn a calendar
 * date into the instant it begins (`startOfLocalDay` — local midnight, or
 * the first instant after it in a zone whose clocks change at midnight) and
 * an instant into the calendar day it falls on (`localDateOf`); the server
 * uses the first to bound its query and the client uses the second to know
 * what "today" is. Everything else here is plain calendar arithmetic — the day
 * after, the Monday of the week, the last day of the month — which never
 * consults a zone, so it is done on UTC dates and cannot drift by an hour.
 *
 * A date is always the string `YYYY-MM-DD`: the same spelling the ETA and
 * appointment date columns use, the `<input type="date">` value, and the
 * query string of the report.
 */

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `true` for a real calendar date spelled `YYYY-MM-DD` (no 30th of February). */
export function isLocalDate(value: string): boolean {
  const match = LOCAL_DATE_PATTERN.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
  );
}

/** The date's fields as a UTC instant at 00:00 — the form the arithmetic below works in. */
function toUtcMidnight(date: string): Date {
  const match = LOCAL_DATE_PATTERN.exec(date);
  if (!match) throw new RangeError(`not a calendar date: ${date}`);
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function fromUtcFields(instant: Date): string {
  const year = String(instant.getUTCFullYear()).padStart(4, "0");
  const month = String(instant.getUTCMonth() + 1).padStart(2, "0");
  const day = String(instant.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

/** `en-CA` spells a date `YYYY-MM-DD`; one formatter per zone, built on first use. */
function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}

/** The zone's wall-clock reading of `instant`, as the same fields taken as UTC. */
function wallClockAsUtc(instant: Date, timeZone: string): number {
  const parts = zoneFormatter(timeZone).formatToParts(instant);
  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour") % 24,
    field("minute"),
    field("second"),
  );
}

/** The calendar day `instant` falls on in `timeZone`. */
export function localDateOf(instant: Date, timeZone: string): string {
  return fromUtcFields(new Date(wallClockAsUtc(instant, timeZone)));
}

/** Wider than any clock change a zone has ever made at midnight, and the whole search window. */
const MIDNIGHT_SEARCH_MS = 3 * 60 * 60 * 1000;

/**
 * The instant at which `date` begins in `timeZone`: the first instant that
 * reads as that calendar day there.
 *
 * The zone's offset is only known for an instant, and the instant is what is
 * being looked for, so a candidate is found in two steps: read the offset at
 * the date's UTC midnight, subtract it, then read the offset again at that
 * candidate — which is right unless the candidate landed on the other side
 * of a DST change, in which case the second reading is. Chicago changes its
 * clocks at 02:00, so there the candidate is the answer. `DEALERSHIP_TIMEZONE`
 * accepts any IANA zone, though, and some change theirs *at* midnight (Chile,
 * Cuba; Brazil until 2019): 00:00 then either does not exist — the day begins
 * at 01:00 — or exists twice — the day begins at the first one. So the
 * candidate is only a seed: the boundary between "the day before" and `date`
 * is then found by bisection over the three hours around it, which settles
 * both a gap and a fold and costs a couple of dozen zone readings.
 */
export function startOfLocalDay(date: string, timeZone: string): Date {
  const wall = toUtcMidnight(date).getTime();
  const firstGuess = wall - (wallClockAsUtc(new Date(wall), timeZone) - wall);
  const offset = wallClockAsUtc(new Date(firstGuess), timeZone) - firstGuess;
  const candidate = wall - offset;

  const isBefore = (instant: number): boolean => localDateOf(new Date(instant), timeZone) < date;
  // `low` reads as the day before, `high` as `date`; the answer is the first instant that does.
  let low = isBefore(candidate) ? candidate : candidate - MIDNIGHT_SEARCH_MS;
  let high = isBefore(candidate) ? candidate + MIDNIGHT_SEARCH_MS : candidate;
  if (!isBefore(low) || isBefore(high)) throw new RangeError(`no midnight found for ${date} in ${timeZone}`);
  while (high - low > 1) {
    const middle = Math.floor((low + high) / 2);
    if (isBefore(middle)) low = middle;
    else high = middle;
  }
  return new Date(high);
}

export type LocalDayRange = Readonly<{
  /** Local midnight at the start of `from` (inclusive). */
  startsAt: Date;
  /** Local midnight at the end of `to`, i.e. the start of the day after (exclusive). */
  endsAt: Date;
}>;

/** The half-open instant window `[start of from, start of the day after to)`. */
export function localDayRange(from: string, to: string, timeZone: string): LocalDayRange {
  return {
    startsAt: startOfLocalDay(from, timeZone),
    endsAt: startOfLocalDay(addDays(to, 1), timeZone),
  };
}

/** `date` moved by `days` (negative for earlier), across month and year ends. */
export function addDays(date: string, days: number): string {
  return fromUtcFields(new Date(toUtcMidnight(date).getTime() + days * DAY_MS));
}

/** How many calendar days `from`..`to` covers, both ends included; 0 when `to` is earlier. */
export function daysInclusive(from: string, to: string): number {
  const span = Math.round((toUtcMidnight(to).getTime() - toUtcMidnight(from).getTime()) / DAY_MS);
  return span < 0 ? 0 : span + 1;
}

/** The Monday of the week `date` is in (a Sunday closes the week that began six days earlier). */
export function startOfWeek(date: string): string {
  const weekday = toUtcMidnight(date).getUTCDay(); // 0 = Sunday
  return addDays(date, weekday === 0 ? -6 : 1 - weekday);
}

/** The Sunday of the week `date` is in. */
export function endOfWeek(date: string): string {
  return addDays(startOfWeek(date), 6);
}

export function startOfMonth(date: string): string {
  return `${date.slice(0, 8)}01`;
}

export function endOfMonth(date: string): string {
  const first = toUtcMidnight(startOfMonth(date));
  // Day 0 of the next month is the last day of this one.
  return fromUtcFields(new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)));
}
